export type FieldRequirement =
  "required" | "column_required" | "conditional" | "optional";

export interface RegistryField {
  key: string;
  label: string;
  type: string;
  requirement: FieldRequirement;
  help?: string;
}

export interface Registry {
  fields: RegistryField[];
  rules: { message: string; fields: string[]; blocks_mapping: boolean }[];
  repeating?: { prefix: string; max_slot: number };
}

export interface PreviewItem {
  key: string;
  sample: unknown;
  imported: string | null;
  status: "ok" | "warning" | "error";
  message: string | null;
}

export interface MappingResponse {
  batch_id: number;
  status: string;
  awaiting_mapping: boolean;
  remappable: boolean;
  columns: string[];
  sample_rows: unknown[][] | null;
  // Server-picked preview values: one saved row, or each column's first non-blank value (index null).
  sample?: { index: number | null; count: number; values: unknown[] };
  registry: Registry;
  mapping: Record<string, string>;
  errors: string[];
  missing_required: string[];
  blocking_rules: string[];
  startable: boolean;
  preview: PreviewItem[];
  /** Fields a file replace unmapped, listed until the mapping is saved again. */
  dropped_fields?: { key: string; label: string; column: string }[];
}

export interface MappingRow {
  index: number;
  header: string;
  sample: unknown;
  fieldKey: string | null;
  // Mapping is keyed by header text, so a blank or repeated header can't be told apart.
  mappable: boolean;
}

export function normalizeHeader(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, " ");
}

export function buildRows(
  columns: string[],
  sampleRow: unknown[] | undefined,
  fields: Record<string, string>
): MappingRow[] {
  const headers = columns.map(normalizeHeader);
  const keyByHeader = new Map(
    Object.entries(fields).map(([key, header]) => [
      normalizeHeader(header),
      key
    ])
  );

  return headers.map((header, index) => {
    const mappable =
      header !== "" && headers.indexOf(header) === headers.lastIndexOf(header);
    return {
      index,
      header: columns[index],
      sample: sampleRow?.[index],
      fieldKey: mappable ? (keyByHeader.get(header) ?? null) : null,
      mappable
    };
  });
}

function slotOf(key: string, prefix: string): number | null {
  const match = key.match(new RegExp(`^${prefix}(\\d+)_`));
  return match ? Number(match[1]) : null;
}

// Slot 1 is declared; later slots copy it as optional fields, one past the highest mapped slot.
export function availableFields(
  registry: Registry,
  fields: Record<string, string>
): RegistryField[] {
  const { repeating } = registry;
  if (!repeating) return registry.fields;

  const { prefix, max_slot: maxSlot } = repeating;
  const templates = registry.fields.filter(
    (field) => slotOf(field.key, prefix) === 1
  );
  const highest = Math.max(
    1,
    ...Object.keys(fields).map((key) => slotOf(key, prefix) ?? 1)
  );
  const lastSlot = Math.min(highest + 1, maxSlot);

  const repeated: RegistryField[] = [];
  for (let slot = 2; slot <= lastSlot; slot += 1) {
    templates.forEach((template) =>
      repeated.push({
        ...template,
        key: template.key.replace(`${prefix}1_`, `${prefix}${slot}_`),
        label: template.label.replace(/\d+/, String(slot)),
        requirement: "optional"
      })
    );
  }
  return [...registry.fields, ...repeated];
}

export function withColumn(
  fields: Record<string, string>,
  header: string,
  fieldKey: string | null
): Record<string, string> {
  const target = normalizeHeader(header);
  const next = Object.fromEntries(
    Object.entries(fields).filter(
      ([key, mapped]) => normalizeHeader(mapped) !== target && key !== fieldKey
    )
  );
  if (fieldKey) next[fieldKey] = target;
  return next;
}
